import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import useApi from 'hooks/useApi';
import { selectCurrentClient } from 'redux/actions/clientActions';
import { setSelectNewDocument } from 'redux/actions/documentActions';

import BackgroundBox from 'shared/components/BackgroundBox';
import { LabelIcon } from 'shared/components/Labels';
import { Button, ButtonLabelIcon } from 'shared/components/Buttons';
import { ReactComponent as RefreshIcon } from 'assets/icon-rotate.svg';
import { ReactComponent as DocumentIcon } from 'assets/icon-document.svg';
import { ReactComponent as AddIcon } from 'assets/icon-add-round.svg';
import { SEARCH_ALL_SITE_OPTION, SEARCH_CLIENT_OPTION } from 'redux/reducers/topBarReducer';
import { emptyString } from 'shared/utils';
import { colors } from 'shared/defaultTheme';
import HomeTab from './HomeTab';

const DocumentSectionCard = ({ className, clients }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const LATEST_UPDATES = '1';
  const LATEST_DOCUMENTS = '2';
  const theme = useSelector((state) => state.theme);
  const { cusNavId, isCustomer, clientId, mail } = useSelector((state) => state.auth);
  const { searchOption, searchValue } = useSelector((state) => state.topBar);
  const { isDeleteClientFromHome } = useSelector((state) => state.clientState);
  const history = useHistory();
  const { customAreaApi } = useApi();
  const { mainColor } = theme;
  const [activeTab, setActiveTab] = useState(LATEST_UPDATES);

  const [lastCreatedDocuments, setLastCreatedDocuments] = useState([]);
  const [lastUpdatedDocuments, setLastUpdatedDocuments] = useState([]);

  const [{ loading: loadingDocuments }, getDocuments] = customAreaApi(
    {
      url: `${window.API_V1}/cli/items/${cusNavId}/${encodeURIComponent(mail)}/search/all`,
      method: 'POST',
    },
    { manual: true }
  );

  const getLatestHigh = async (searchClient = undefined) => {
    try {
      const filters = [
        {
          model: 'PathItem',
          field: 'IsFile',
          filter: [true],
        },
      ];
      if (searchClient) {
        filters.push({
          model: 'PathItem',
          field: 'ClientId',
          filter: [searchClient],
        });
      }
      if (!isCustomer) {
        filters.push({
          model: 'PathItem',
          field: 'ClientId',
          filter: [clientId],
        });
      }

      filters.push({
        model: 'PathItem',
        field: 'Created',
        asc: false,
      });

      const {
        data: { data },
      } = await getDocuments({
        data: {
          filters,
          reduce: {
            limit: 8,
            skip: 0,
          },
        },
      });
      setLastCreatedDocuments(data || []);
    } catch (ex) {
      setLastCreatedDocuments([]);
    }
  };

  const getLatestMovement = async (searchClient = undefined) => {
    try {
      const filters = [
        {
          model: 'PathItem',
          field: 'IsFile',
          filter: [true],
        },
      ];
      if (searchClient) {
        filters.push({
          model: 'PathItem',
          field: 'ClientId',
          filter: [searchClient],
        });
      }
      if (!isCustomer) {
        filters.push({
          model: 'PathItem',
          field: 'ClientId',
          filter: [clientId],
        });
      }

      filters.push({
        model: 'PathItem',
        field: 'Modified',
        asc: false,
      });

      const {
        data: { data },
      } = await getDocuments({
        data: {
          filters,
          reduce: {
            limit: 8,
            skip: 0,
          },
        },
      });
      setLastUpdatedDocuments(data || []);
    } catch (ex) {
      setLastUpdatedDocuments([]);
    }
  };

  const refreshDocumentsData = async () => {
    if (activeTab === LATEST_UPDATES) {
      await getLatestMovement(searchValue || undefined);
    }
    if (activeTab === LATEST_DOCUMENTS) {
      await getLatestHigh(searchValue || undefined);
    }
  };

  useEffect(() => {
    (async () => {
      await refreshDocumentsData();
    })();
  }, [searchOption, searchValue]);

  useEffect(() => {
    (async () => {
      if (isDeleteClientFromHome) {
        await refreshDocumentsData();
      }
    })();
  }, [isDeleteClientFromHome]);

  useEffect(() => {
    (async () => {
      await refreshDocumentsData();
    })();
  }, [activeTab]);

  useEffect(() => {
    (async () => {
      if (isDeleteClientFromHome) {
        if (SEARCH_CLIENT_OPTION === searchOption && !emptyString(searchValue)) {
          await refreshDocumentsData(searchValue);
        }
        if (SEARCH_CLIENT_OPTION === searchOption && emptyString(searchValue)) {
          await refreshDocumentsData();
        }
        if (SEARCH_ALL_SITE_OPTION === searchOption) {
          await refreshDocumentsData();
        }
      }
    })();
  }, [searchOption, searchValue, activeTab]);

  const toggleDocument = async (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <BackgroundBox
      className={classNames(['ml-background-dashboard background-box__flex-home', className])}
    >
      <div className="d-flex justify-content-between home__mb-document">
        <div onClick={() => history.push('/documentations')} aria-hidden="true">
          <LabelIcon
            label={t('general_documentation')}
            Icon={() => <DocumentIcon className="label-icon__document" fill={colors.white} />}
            color={mainColor}
            className="ml-1 label-icon text-uppercase"
            classNameMain=""
            isDashboard
          />
        </div>

        <div className="d-flex w-100 justify-content-end">
          <ButtonLabelIcon
            id="add-icon-notification"
            text={t('general_button_new')}
            type="button"
            Icon={() => <AddIcon fill={colors.black} />}
            className="button-icon mr-1"
            onClick={() => {
              dispatch(selectCurrentClient(undefined));
              if (isCustomer) {
                const showMessageAgain = document.cookie.indexOf('showMessageAgain');
                if (showMessageAgain !== -1) {
                  dispatch(setSelectNewDocument(false));
                } else {
                  dispatch(setSelectNewDocument(true));
                }
              }
              history.push('/documentations');
            }}
          />
          <ButtonLabelIcon
            id="refresh-icon-mail"
            text={t('general_button_refresh')}
            type="button"
            onClick={refreshDocumentsData}
            Icon={() => <RefreshIcon fill={colors.black} />}
            className="button-icon"
          />
        </div>
      </div>
      {!loadingDocuments ? (
        <>
          <HomeTab
            clients={clients}
            activeTab={activeTab}
            toggleDocument={toggleDocument}
            refreshDocumentsData={refreshDocumentsData}
            lastCreatedDocuments={lastCreatedDocuments}
            lastUpdatedDocuments={lastUpdatedDocuments}
          />
          {(lastCreatedDocuments.length > 0 || lastUpdatedDocuments.length > 0) && (
            <div className="action-btn">
              <Button
                className="home__btn text-uppercase"
                type="submit"
                onClick={() => {
                  dispatch(selectCurrentClient(undefined));
                  history.push('/documentations');
                }}
                label={t('general_see_all')}
                color="primary"
              />
            </div>
          )}
        </>
      ) : (
        <div className="d-flex justify-content-center pt-5 mb-5">
          <Spinner color={mainColor} />
        </div>
      )}
    </BackgroundBox>
  );
};

DocumentSectionCard.defaultProps = {
  className: '',
};

DocumentSectionCard.propTypes = {
  className: PropTypes.string,
  clients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
};

export default DocumentSectionCard;
